import { useEffect, useId, useState } from "react";
import L from "leaflet";
import proj4, { type Converter } from "proj4";

/**
 * QGIS-style map status bar: live cursor coordinates (editable — Enter recenters
 * the map there), current scale (editable — Enter zooms to it) and the display
 * EPSG. The Leaflet map itself always runs WGS84/Web Mercator — the chosen EPSG
 * only changes how coordinates are DISPLAYED and PARSED.
 */

export interface CrsOption {
  code: number;
  /** Short human name shown next to the EPSG code. */
  name: string;
  /** proj4 definition or registry name (null = WGS84 passthrough). */
  def: string | null;
  /** Fraction digits used when formatting coordinates in this CRS. */
  decimals: number;
  /**
   * Authority axis order puts northing/latitude FIRST — modern QGIS honors it
   * (EPSG:4326 shows lat,lon; PL-1992/PL-2000 show X=northing first, matching
   * the Polish surveying convention), so we do too.
   */
  northingFirst: boolean;
  /** Axis-order hint for the input tooltip (Polish UI). */
  hint: string;
}

// PL-1992 / PL-2000 definitions mirror backend GUGiK usage; display-only here.
const TMERC =
  "+proj=tmerc +lat_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs";
// Hints are written in the accepted input format: two values, space-separated.
const PL_HINT = "X (północ) Y (wschód) [m]";

export const MAP_CRS_OPTIONS: CrsOption[] = [
  { code: 2180, name: "PL-1992", def: `${TMERC} +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000`, decimals: 2, northingFirst: true, hint: PL_HINT },
  { code: 2176, name: "PL-2000/15", def: `${TMERC} +lon_0=15 +k=0.999923 +x_0=5500000 +y_0=0`, decimals: 2, northingFirst: true, hint: PL_HINT },
  { code: 2177, name: "PL-2000/18", def: `${TMERC} +lon_0=18 +k=0.999923 +x_0=6500000 +y_0=0`, decimals: 2, northingFirst: true, hint: PL_HINT },
  { code: 2178, name: "PL-2000/21", def: `${TMERC} +lon_0=21 +k=0.999923 +x_0=7500000 +y_0=0`, decimals: 2, northingFirst: true, hint: PL_HINT },
  { code: 2179, name: "PL-2000/24", def: `${TMERC} +lon_0=24 +k=0.999923 +x_0=8500000 +y_0=0`, decimals: 2, northingFirst: true, hint: PL_HINT },
  { code: 4326, name: "WGS 84", def: null, decimals: 6, northingFirst: true, hint: "szerokość długość [°]" },
  { code: 3857, name: "Web Mercator", def: "EPSG:3857", decimals: 2, northingFirst: false, hint: "X (wschód) Y (północ) [m]" },
];

const DEFAULT_CRS_CODE = 2180;

const projCache = new Map<number, Converter>();
function converterFor(crs: CrsOption): Converter | null {
  if (!crs.def) return null;
  let conv = projCache.get(crs.code);
  if (!conv) {
    conv = proj4(crs.def);
    projCache.set(crs.code, conv);
  }
  return conv;
}

/** WGS84 → coordinate pair in the CRS's DISPLAY axis order. */
export function latLngToDisplay(
  crs: CrsOption,
  ll: { lat: number; lng: number },
): [number, number] {
  const conv = converterFor(crs);
  const [e, n] = conv ? conv.forward([ll.lng, ll.lat]) : [ll.lng, ll.lat];
  return crs.northingFirst ? [n, e] : [e, n];
}

/** Coordinate pair in DISPLAY axis order → WGS84; null when out of this world. */
export function displayToLatLng(
  crs: CrsOption,
  pair: [number, number],
): { lat: number; lng: number } | null {
  const [a, b] = pair;
  const [e, n] = crs.northingFirst ? [b, a] : [a, b];
  const conv = converterFor(crs);
  const [lng, lat] = conv ? conv.inverse([e, n]) : [e, n];
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  // Web Mercator can't show the poles; beyond that the input is a typo anyway.
  if (Math.abs(lat) > 85 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/** Space-separated, like QGIS — a comma here would fight the Polish decimal comma. */
export function formatCoordPair(crs: CrsOption, ll: { lat: number; lng: number }): string {
  const [a, b] = latLngToDisplay(crs, ll);
  return `${a.toFixed(crs.decimals)} ${b.toFixed(crs.decimals)}`;
}

/** Only once the separator is known can a comma be read as a decimal comma —
 *  hence the edge-comma strip before the substitution ("52.3, 21.0" pastes fine). */
const toNumber = (token: string): number => {
  const s = token.trim().replace(/^,|,$/g, "").replace(",", ".");
  return s === "" ? NaN : Number(s);
};

/** "a b" (what we render) plus the tolerated "a, b" / "a; b". A whitespace- or
 *  ';'-separated pair may use the Polish decimal comma ("52,3 21,0"). */
export function parseCoordPair(text: string): [number, number] | null {
  const t = text.trim();
  let parts: string[];
  if (t.includes(";")) parts = t.split(";");
  else if (/\s/.test(t)) parts = t.split(/\s+/);
  else parts = t.split(",");
  if (parts.length !== 2 || parts.some((p) => p.trim() === "")) return null;
  const [a, b] = parts.map(toNumber);
  return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null;
}

// Screen scale assumes the CSS reference 96 dpi — same as QGIS's default.
const EQUATOR_M = 40075016.686;
const PX_PER_PAPER_M = 96 / 0.0254;

/** Denominator of the current map scale (1:denominator) at a given latitude. */
export function scaleDenominator(zoom: number, lat: number): number {
  const mPerPx = (EQUATOR_M * Math.cos((lat * Math.PI) / 180)) / 2 ** (zoom + 8);
  return mPerPx * PX_PER_PAPER_M;
}

/** Inverse of scaleDenominator: the (fractional) zoom showing 1:denom. */
export function zoomForScale(denom: number, lat: number): number {
  return Math.log2((EQUATOR_M * Math.cos((lat * Math.PI) / 180) * PX_PER_PAPER_M) / denom) - 8;
}

/** "1:500", "1 : 2 500", "2500" — thousands spaces (incl. nbsp) allowed. */
export function parseScale(text: string): number | null {
  const cleaned = text.replace(/\s/g, "").replace(/^1:/, "");
  if (!/^\d+([.,]\d+)?$/.test(cleaned)) return null;
  const denom = Number(cleaned.replace(",", "."));
  return denom > 0 ? denom : null;
}

/** Plain digits, no thousands grouping — the field is meant to be retyped. */
export function formatScale(denom: number): string {
  return `1:${Math.round(denom)}`;
}

const SCALE_PRESETS = [
  100, 250, 500, 1000, 2000, 5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000,
];

interface Props {
  map: L.Map | null;
  /** localStorage key persisting the chosen EPSG. */
  storageKey: string;
  className?: string;
}

export function MapStatusBar({ map, storageKey, className }: Props) {
  const scaleListId = useId();
  const [crsCode, setCrsCode] = useState<number>(() => {
    const stored = Number(localStorage.getItem(storageKey));
    return MAP_CRS_OPTIONS.some((c) => c.code === stored) ? stored : DEFAULT_CRS_CODE;
  });
  const crs = MAP_CRS_OPTIONS.find((c) => c.code === crsCode) ?? MAP_CRS_OPTIONS[0];
  useEffect(() => {
    localStorage.setItem(storageKey, String(crsCode));
  }, [crsCode, storageKey]);

  const [cursor, setCursor] = useState<L.LatLng | null>(null);
  // Bumped on zoom/pan so the scale (and center-based fallback) re-renders.
  const [, setViewEpoch] = useState(0);
  // While an input is focused its draft wins over the live value.
  const [coordDraft, setCoordDraft] = useState<string | null>(null);
  const [scaleDraft, setScaleDraft] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<"coord" | "scale" | null>(null);

  useEffect(() => {
    if (!map) return;
    const onMouse = (e: L.LeafletMouseEvent) => setCursor(e.latlng);
    const onView = () => setViewEpoch((n) => n + 1);
    map.on("mousemove", onMouse);
    map.on("zoom", onView);
    map.on("moveend", onView);
    return () => {
      map.off("mousemove", onMouse);
      map.off("zoom", onView);
      map.off("moveend", onView);
    };
  }, [map]);

  if (!map) return null;

  const coordText = formatCoordPair(crs, cursor ?? map.getCenter());
  const scaleText = formatScale(scaleDenominator(map.getZoom(), map.getCenter().lat));

  const commitCoords = (text: string) => {
    const pair = parseCoordPair(text);
    const ll = pair ? displayToLatLng(crs, pair) : null;
    if (!ll) {
      setInvalid("coord");
      return false;
    }
    map.panTo(ll);
    return true;
  };

  const commitScale = (text: string) => {
    const denom = parseScale(text);
    if (!denom) {
      setInvalid("scale");
      return false;
    }
    const zoom = zoomForScale(denom, map.getCenter().lat);
    const clamped = Math.max(map.getMinZoom(), Math.min(map.getMaxZoom(), zoom));
    // Leaflet snaps setZoom to zoomSnap; an exact typed scale needs a bypass.
    const prevSnap = map.options.zoomSnap;
    map.options.zoomSnap = 0;
    map.setZoom(clamped, { animate: false });
    map.options.zoomSnap = prevSnap;
    return true;
  };

  const inputClass = (bad: boolean) =>
    `input-base h-5 border px-1.5 py-0 font-mono text-[11px] ${
      bad ? "border-red-500" : "border-[var(--color-border)]"
    }`;

  return (
    <div
      className={`flex h-7 shrink-0 items-center gap-2 border-t border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 font-mono text-[11px] text-[var(--color-text-secondary)] ${className ?? ""}`}
    >
      <label className="flex min-w-0 items-center gap-1.5">
        <span className="shrink-0 text-[var(--color-text-tertiary)]">Współrzędne</span>
        <input
          type="text"
          value={coordDraft ?? coordText}
          title={`${crs.hint} — Enter przenosi widok w to miejsce`}
          onFocus={(e) => {
            setCoordDraft(coordText);
            e.currentTarget.select();
          }}
          onChange={(e) => {
            setCoordDraft(e.target.value);
            setInvalid((v) => (v === "coord" ? null : v));
          }}
          onBlur={() => {
            setCoordDraft(null);
            setInvalid((v) => (v === "coord" ? null : v));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && commitCoords(e.currentTarget.value)) e.currentTarget.blur();
            if (e.key === "Escape") e.currentTarget.blur();
          }}
          className={`${inputClass(invalid === "coord")} w-56`}
        />
      </label>

      <div className="min-w-0 flex-1" />

      <label className="flex shrink-0 items-center gap-1.5">
        <span className="text-[var(--color-text-tertiary)]">Skala</span>
        <input
          type="text"
          value={scaleDraft ?? scaleText}
          list={scaleListId}
          title="Np. 1:500 — Enter ustawia skalę mapy"
          onFocus={(e) => {
            setScaleDraft(scaleText);
            e.currentTarget.select();
          }}
          onChange={(e) => {
            setScaleDraft(e.target.value);
            setInvalid((v) => (v === "scale" ? null : v));
          }}
          onBlur={() => {
            setScaleDraft(null);
            setInvalid((v) => (v === "scale" ? null : v));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && commitScale(e.currentTarget.value)) e.currentTarget.blur();
            if (e.key === "Escape") e.currentTarget.blur();
          }}
          className={`${inputClass(invalid === "scale")} w-28`}
        />
        <datalist id={scaleListId}>
          {SCALE_PRESETS.map((d) => (
            <option key={d} value={`1:${d}`} />
          ))}
        </datalist>
      </label>

      <label className="flex shrink-0 items-center gap-1.5" title="Układ współrzędnych wyświetlania (mapa zawsze rysuje w WGS84)">
        <select
          value={crsCode}
          onChange={(e) => setCrsCode(Number(e.target.value))}
          className="input-base h-5 w-auto border border-[var(--color-border)] px-1.5 py-0 font-mono text-[11px]"
        >
          {MAP_CRS_OPTIONS.map((c) => (
            <option key={c.code} value={c.code}>
              EPSG:{c.code} ({c.name})
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
